import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Calendar, DollarSign, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export default function Dashboard() {
  const [sponsorshipData, setSponsorshipData] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);

  useEffect(() => {
    fetchSponsorshipData();
    fetchMembers();
  }, []);

  const fetchSponsorshipData = async () => {
    const { data: ptaData } = await supabase
      .from('ptas')
      .select('id')
      .single();

    if (!ptaData?.id) return;

    const { data } = await supabase
      .from('sponsorships')
      .select('*')
      .eq('pta_id', ptaData.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    setSponsorshipData(data);
  };

  const fetchMembers = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get the user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    // Fetch all members of the PTA
    const { data: membersData } = await supabase
      .from('members')
      .select('id, full_name, email, user_id')
      .eq('pta_id', memberData.pta_id);

    if (!membersData) return;

    // Get profiles and roles for each member
    const membersWithRoles = await Promise.all(
      membersData.map(async (member) => {
        // Get profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('hide_avatar_from_directory, avatar_url')
          .eq('id', member.user_id)
          .maybeSingle();

        // Skip if member opted out
        if (profile?.hide_avatar_from_directory) return null;

        // Get roles
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', member.user_id);

        const roleNames = roles?.map(r => {
          // Format role names nicely
          const roleMap: { [key: string]: string } = {
            'chair': 'Chair',
            'treasurer': 'Treasurer',
            'secretary': 'Secretary',
            'events': 'Events Coordinator',
            'catering': 'Catering Coordinator',
            'communications': 'Communications',
            'community': 'Community Liaison',
            'school_liaison': 'School Liaison',
            'trustee': 'Trustee',
            'shop': 'Shop Manager',
          };
          return roleMap[r.role] || r.role;
        }) || [];

        return {
          name: member.full_name,
          role: roleNames.length > 0 ? roleNames.join(', ') : 'Member',
          email: member.email,
          avatar_url: profile?.avatar_url
        };
      })
    );

    setMembers(membersWithRoles.filter(m => m !== null));
  };

  const stats = [
    {
      title: "Total Members",
      value: "24",
      description: "Active PTA members",
      icon: Users,
    },
    {
      title: "Upcoming Events",
      value: "3",
      description: "In the next 30 days",
      icon: Calendar,
    },
    {
      title: "Funds Raised",
      value: "£2,450",
      description: "This year",
      icon: DollarSign,
    },
    {
      title: "Goals",
      value: "3",
      description: "Active goals",
      icon: Package,
    },
  ];

  const upcomingEvents = [
    { name: "Summer Fair", date: "June 15, 2025", time: "2:00 PM" },
    { name: "Committee Meeting", date: "April 10, 2025", time: "6:30 PM" },
    { name: "Bake Sale", date: "May 10, 2025", time: "3:00 PM" },
  ];

  const expenseClaims = [
    { name: "Sarah Smith", description: "Supplies for Bake Sale", amount: "£42.50", status: "pending" },
    { name: "John Doe", description: "Venue Deposit", amount: "£100.00", status: "paid" },
  ];


  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-foreground">PTA Dashboard</h1>
      </div>

      {sponsorshipData && (
        <Card className="shadow-card border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="pt-6">
            <p className="text-center text-lg">
              <span className="text-muted-foreground">Membership Sponsored by:</span>{' '}
              <span className="font-bold text-primary">{sponsorshipData.business_name}</span>
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5">
                <CardTitle className="text-xs font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-1">
                <div className="text-xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="members">Members Directory</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Fundraising Progress</CardTitle>
                <CardDescription>Current year goals</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Playground Equipment</span>
                      <span className="text-sm text-primary">£3,000 / £5,000</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: "60%" }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Library Books</span>
                      <span className="text-sm text-primary">£450 / £1,500</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: "30%" }} />
                    </div>
                  </div>
                </div>
                <Link to="/dashboard/chair">
                  <Button className="w-full mt-4" variant="outline">View All Goals</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Expense Claims</CardTitle>
                <CardDescription>Pending reimbursements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {expenseClaims.map((claim, i) => (
                    <div key={i} className="p-3 border border-border rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{claim.name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          claim.status === "paid" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {claim.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{claim.description}</p>
                      <p className="text-sm font-semibold text-primary">{claim.amount}</p>
                    </div>
                  ))}
                </div>
                <Link to="/dashboard/treasurer">
                  <Button className="w-full mt-4" variant="outline">View All Claims</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Upcoming Events</CardTitle>
                <CardDescription>Next 3 scheduled events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcomingEvents.map((event, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <Calendar className="h-5 w-5 text-primary mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">{event.name}</p>
                        <p className="text-sm text-muted-foreground">{event.date} at {event.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/dashboard/events">
                  <Button className="w-full mt-4" variant="outline">View All Events</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest updates from your PTA</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">New member joined</p>
                      <p className="text-xs text-muted-foreground">Sarah Smith - 2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Event scheduled</p>
                      <p className="text-xs text-muted-foreground">Summer Fair - Yesterday</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Fundraiser completed</p>
                      <p className="text-xs text-muted-foreground">Bake Sale - 3 days ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="calendar" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>PTA Calendar</CardTitle>
              <CardDescription>Auto-synced events and meetings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Calendar Sync</h4>
                    <Button variant="outline" size="sm">
                      <Calendar className="h-4 w-4 mr-2" />
                      Sync to Google Calendar
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Automatically sync PTA events and meetings to your personal calendar
                  </p>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">June 2025</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-accent/50 rounded">
                      <div className="text-center">
                        <div className="text-2xl font-bold">15</div>
                        <div className="text-xs text-muted-foreground">JUN</div>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Summer Fair</p>
                        <p className="text-sm text-muted-foreground">2:00 PM - School Field</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Members Directory</CardTitle>
              <CardDescription>PTA members and roles (members can opt-out)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {members.length > 0 ? (
                  members.map((member, i) => (
                    <div key={i} className="p-4 border border-border rounded-lg hover:shadow-card transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {member.avatar_url ? (
                            <img 
                              src={member.avatar_url} 
                              alt={member.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground font-semibold">
                              {member.name.split(' ').map((n: string) => n[0]).join('')}
                            </div>
                          )}
                          <div>
                            <h4 className="font-semibold">{member.name}</h4>
                            <p className="text-sm text-muted-foreground">{member.role}</p>
                            <p className="text-sm text-primary mt-1">{member.email}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-8 text-muted-foreground">
                    No members found in the directory
                  </div>
                )}
              </div>
              <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Members can opt-out of the directory in their profile settings
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
